import React, { useState, useRef, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import { usePrivacy } from '../../contexts/PrivacyContext';
import { useCurrency, DISPLAY_CURRENCIES } from '../../contexts/CurrencyContext';
import logoDarkBlue from '../../assets/logos/logo-darkblue-nobg.png';

const navigation = [
  { name: 'Dashboard', to: '/' },
  { name: 'Portfolios', to: '/portfolios' },
  { name: 'ETFs', to: '/etfs' },
];

const navLinkStyle = (isActive: boolean): React.CSSProperties => ({
  padding: '6px 12px',
  borderRadius: '6px',
  fontSize: '0.875rem',
  fontWeight: isActive ? 600 : 400,
  color: isActive ? '#000537' : '#71717a',
  backgroundColor: isActive ? '#f4f4f5' : 'transparent',
  textDecoration: 'none',
  transition: 'all 0.15s ease',
  whiteSpace: 'nowrap',
});

const Navbar: React.FC = () => {
  const { user, logout } = useAuth();
  const { isPrivateMode, togglePrivateMode } = usePrivacy();
  const { displayCurrency, setDisplayCurrency } = useCurrency();

  const [accountOpen, setAccountOpen] = useState(false);
  const [currencyExpanded, setCurrencyExpanded] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const accountRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) {
        setAccountOpen(false);
        setCurrencyExpanded(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSignOut = async () => {
    setAccountOpen(false);
    await logout();
  };

  const getInitial = (email: string) => email?.[0]?.toUpperCase() ?? '?';

  return (
    <>
      <nav
        className="fixed-top"
        style={{
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #f4f4f5',
          height: '56px',
          zIndex: 1030,
          boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', height: '100%', padding: '0 20px', gap: '8px' }}>
          {/* Logo */}
          <Link to="/" style={{ flexShrink: 0, display: 'flex', alignItems: 'center', marginRight: '8px' }}>
            <img src={logoDarkBlue} alt="Aumora" height="36" />
          </Link>

          {/* Desktop nav links */}
          <div className="d-none d-md-flex" style={{ gap: '2px', flex: 1 }}>
            {navigation.map(item => (
              <NavLink
                key={item.name}
                to={item.to}
                end={item.to === '/'}
                style={({ isActive }) => navLinkStyle(isActive)}
              >
                {item.name}
              </NavLink>
            ))}
          </div>

          {/* Spacer on mobile */}
          <div className="d-md-none" style={{ flex: 1 }} />

          {/* Account dropdown */}
          {user && (
            <div ref={accountRef} style={{ position: 'relative', flexShrink: 0 }}>
              <button
                onClick={() => { setAccountOpen(o => !o); if (accountOpen) setCurrencyExpanded(false); }}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: '#000537',
                  color: '#ffffff',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {getInitial(user.email)}
              </button>

              {accountOpen && (
                <div style={{
                  position: 'absolute',
                  top: 'calc(100% + 8px)',
                  right: 0,
                  width: '220px',
                  background: '#ffffff',
                  border: '1px solid #e4e4e7',
                  borderRadius: '10px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                  zIndex: 1050,
                  overflow: 'hidden',
                }}>
                  {/* User info */}
                  <div style={{ padding: '12px 16px', borderBottom: '1px solid #f4f4f5' }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#18181b' }}>
                      {user.email.split('@')[0]}
                    </div>
                    <div style={{ fontSize: '0.72rem', color: '#a1a1aa', marginTop: '1px' }}>{user.email}</div>
                  </div>

                  {/* Currency */}
                  <button
                    onClick={() => setCurrencyExpanded(e => !e)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '9px 16px',
                      background: 'transparent',
                      border: 0,
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      color: '#52525b',
                      borderBottom: currencyExpanded ? 'none' : '1px solid #f4f4f5',
                    }}
                  >
                    <span style={{ flex: 1, textAlign: 'left' }}>Currency</span>
                    <span style={{
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      padding: '2px 6px',
                      borderRadius: '999px',
                      backgroundColor: '#000537',
                      color: '#ffffff',
                      letterSpacing: '0.04em',
                    }}>
                      {displayCurrency}
                    </span>
                    <svg
                      width="10" height="10" fill="currentColor" viewBox="0 0 16 16"
                      style={{ color: '#a1a1aa', transform: currencyExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
                    >
                      <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z"/>
                    </svg>
                  </button>

                  {currencyExpanded && (
                    <div style={{ borderBottom: '1px solid #f4f4f5', paddingBottom: '4px' }}>
                      {DISPLAY_CURRENCIES.map(c => (
                        <button
                          key={c}
                          onClick={() => { setDisplayCurrency(c); setCurrencyExpanded(false); }}
                          style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '6px 16px 6px 28px',
                            background: 'transparent',
                            border: 0,
                            cursor: 'pointer',
                            fontSize: '0.82rem',
                            color: c === displayCurrency ? '#000537' : '#52525b',
                            fontWeight: c === displayCurrency ? 600 : 400,
                          }}
                          onMouseEnter={e => { if (c !== displayCurrency) e.currentTarget.style.background = '#fafafa'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                        >
                          {c}
                          {c === displayCurrency && (
                            <svg width="10" height="10" fill="currentColor" viewBox="0 0 16 16" style={{ color: '#000537' }}>
                              <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                            </svg>
                          )}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Privacy mode */}
                  <button
                    onClick={togglePrivateMode}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '9px 16px',
                      background: 'transparent',
                      border: 0,
                      borderBottom: '1px solid #f4f4f5',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      color: '#52525b',
                    }}
                  >
                    <span style={{ flex: 1, textAlign: 'left' }}>Private Mode</span>
                    <span style={{
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      padding: '2px 6px',
                      borderRadius: '999px',
                      backgroundColor: isPrivateMode ? '#000537' : '#f4f4f5',
                      color: isPrivateMode ? '#ffffff' : '#a1a1aa',
                      letterSpacing: '0.04em',
                    }}>
                      {isPrivateMode ? 'ON' : 'OFF'}
                    </span>
                  </button>

                  {/* Sign out */}
                  <button
                    onClick={handleSignOut}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: '9px 16px',
                      background: 'transparent',
                      border: 0,
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      color: '#dc2626',
                    }}
                  >
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Mobile hamburger */}
          <button
            className="d-md-none"
            onClick={() => setMobileNavOpen(o => !o)}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '6px',
              color: '#000537',
              display: 'flex',
              alignItems: 'center',
              marginLeft: '4px',
            }}
            aria-label="Toggle navigation"
          >
            {mobileNavOpen ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"/>
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                <path fillRule="evenodd" d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5z"/>
              </svg>
            )}
          </button>
        </div>
      </nav>

      {/* Mobile nav drawer */}
      {mobileNavOpen && (
        <div
          className="d-md-none fixed-top"
          style={{
            top: '56px',
            left: 0,
            right: 0,
            background: '#ffffff',
            borderBottom: '1px solid #f4f4f5',
            padding: '8px 12px',
            zIndex: 1029,
            boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
          }}
        >
          {navigation.map(item => (
            <NavLink
              key={item.name}
              to={item.to}
              end={item.to === '/'}
              onClick={() => setMobileNavOpen(false)}
              style={({ isActive }) => ({
                ...navLinkStyle(isActive),
                display: 'block',
                marginBottom: '2px',
              })}
            >
              {item.name}
            </NavLink>
          ))}
        </div>
      )}
    </>
  );
};

export default Navbar;
